import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

interface Comment {
  id: string;
  comment: string;
  created_at: string;
  updated_at: string;
  parent_comment_id?: string;
  user: {
    id: string;
    username: string;
    full_name: string;
  };
}

interface PluginCommentsProps {
  pluginId: string;
  onStatsUpdate?: () => void;
}

export const PluginComments: React.FC<PluginCommentsProps> = ({ pluginId, onStatsUpdate }) => {
  const { user, isAdmin } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingComment, setDeletingComment] = useState<string | null>(null);

  useEffect(() => {
    loadComments();
  }, [pluginId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_plugin_comments', {
        plugin_id_param: pluginId,
        limit_param: 20,
        offset_param: 0
      });

      if (error) {
        console.error('Error loading comments:', error);
        return;
      }

      setComments(data || []);
    } catch (err) {
      console.error('Unexpected error loading comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be signed in to comment');
      return;
    }

    if (newComment.length < 10) {
      setError('Comment must be at least 10 characters long');
      return;
    }

    if (newComment.length > 1000) {
      setError('Comment must be less than 1000 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const { data, error } = await supabase.rpc('add_plugin_comment', {
        plugin_id_param: pluginId,
        comment_param: newComment.trim()
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (data?.success && data?.comment) {
        // Add the new comment to the beginning of the list
        setComments(prev => [data.comment, ...prev]);
        setNewComment('');
        
        // Trigger stats update for comment count
        if (onStatsUpdate) {
          onStatsUpdate();
        }
      } else if (data?.error) {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to submit comment');
      console.error('Error submitting comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !replyingTo || replyText.trim().length < 10) return;

    try {
      setSubmitting(true);
      setError(null);

      // Use the original RPC function for now
      const { data, error } = await supabase.rpc('add_plugin_comment', {
        plugin_id_param: pluginId,
        comment_param: `@${comments.find(c => c.id === replyingTo)?.user.username}: ${replyText.trim()}`
      });

      if (error) throw error;

      // Refresh comments
      await loadComments();
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Error submitting reply:', err);
      setError('Failed to submit reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string, reason?: string) => {
    if (!isAdmin) {
      setError('Only administrators can delete comments');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this comment? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      setDeletingComment(commentId);
      setError(null);

      console.log('Attempting to delete comment:', commentId);

      // First, check if the comment exists and its current status
      const { data: commentCheck, error: checkError } = await supabase
        .from('plugin_comments')
        .select('id, deleted_at, comment')
        .eq('id', commentId)
        .single();

      if (checkError) {
        console.error('Error checking comment:', checkError);
        throw new Error(`Comment lookup failed: ${checkError.message}`);
      }

      if (!commentCheck) {
        throw new Error('Comment not found in database');
      }

      if (commentCheck.deleted_at) {
        throw new Error('Comment is already deleted');
      }

      console.log('Comment found, proceeding with deletion');

      // Try direct database update first (more reliable)
      const { error: updateError } = await supabase
        .from('plugin_comments')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId);

      if (updateError) {
        console.error('Direct update error:', updateError);
        
        // Fall back to RPC function
        const { data, error: rpcError } = await supabase.rpc('admin_delete_comment', {
          comment_id: commentId,
          reason: reason || 'Deleted by admin',
          deletion_type: 'soft'
        });

        if (rpcError) {
          console.error('RPC function error:', rpcError);
          throw new Error(`Failed to delete comment: ${rpcError.message}`);
        }

        if (data && typeof data === 'object' && 'success' in data && !data.success) {
          throw new Error(data.error || 'Unknown error occurred');
        }
      }

      // Reload comments to reflect the deletion
      await loadComments();
      console.log('Comment deleted successfully');
      
      // Trigger stats update for comment count
      if (onStatsUpdate) {
        onStatsUpdate();
      }
      
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      setError(error.message || 'Failed to delete comment. Please try again.');
    } finally {
      setDeletingComment(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getInitials = (user: Comment['user']) => {
    if (user.full_name) {
      return user.full_name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.username.slice(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-2">
              Add a comment
            </label>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts about this plugin... (10-1000 characters)"
              className="w-full p-3 bg-theme-surface-light border-2 border-theme-border rounded-md text-theme-text resize-vertical min-h-[100px] focus:border-theme-primary focus:outline-none transition-colors"
              maxLength={1000}
            />
            <div className="flex justify-between items-center mt-2">
              <span className={`text-sm ${
                newComment.length < 10 ? 'text-theme-text-muted' : 
                newComment.length > 900 ? 'text-theme-warning' : 'text-theme-text-secondary'
              }`}>
                {newComment.length}/1000 characters
              </span>
              {newComment.length > 0 && newComment.length < 10 && (
                <span className="text-sm text-theme-warning">
                  Minimum 10 characters required
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-900/30 border-2 border-dashed border-red-600 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || newComment.length < 10 || newComment.length > 1000}
            className="px-4 py-2 bg-theme-primary text-white hover:bg-theme-secondary transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Posting...
              </>
            ) : (
              <>
                <img 
                  src="https://unpkg.com/pixelarticons@1.8.0/svg/message.svg" 
                  alt="Comment" 
                  className="w-4 h-4"
                  style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                />
                Post Comment
              </>
            )}
          </button>
        </form>
      ) : (
        <div className="p-4 bg-theme-surface-light border-2 border-theme-border rounded-lg text-center">
          <p className="text-theme-text-secondary mb-2">Sign in to leave a comment</p>
          <p className="text-sm text-theme-text-muted">Join the conversation and share your thoughts!</p>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-theme-text">
          Comments ({comments.length})
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3 text-theme-text-secondary">
              <div className="w-6 h-6 border-2 border-theme-text-secondary border-t-transparent rounded-full animate-spin"></div>
              <span>Loading comments...</span>
            </div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-theme-surface-light border-2 border-theme-border rounded-full flex items-center justify-center mx-auto mb-4">
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/message.svg" 
                alt="No comments" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
              />
            </div>
            <p className="text-theme-text-secondary mb-2">No comments yet</p>
            <p className="text-sm text-theme-text-muted">Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.filter(comment => !comment.parent_comment_id).map((comment) => (
              <div key={comment.id} className="bg-theme-surface-light border-2 border-theme-border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-theme-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {getInitials(comment.user)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-theme-text">
                        {comment.user.full_name || comment.user.username}
                      </span>
                      <span className="text-sm text-theme-text-muted">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-theme-text-secondary leading-relaxed">{comment.comment}</p>
                    
                    {/* Action buttons - Reply and Admin Delete */}
                    <div className="flex items-center gap-4 mt-3">
                      {user && replyingTo !== comment.id && (
                        <button
                          onClick={() => setReplyingTo(comment.id)}
                          className="text-sm text-theme-primary hover:text-theme-secondary transition-colors flex items-center gap-1"
                        >
                          <img 
                            src="https://unpkg.com/pixelarticons@1.8.0/svg/reply.svg" 
                            alt="Reply" 
                            className="w-4 h-4"
                            style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                          />
                          Reply
                        </button>
                      )}
                      
                      {/* Admin Delete Button */}
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={deletingComment === comment.id}
                          className="text-sm text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete comment (Admin only)"
                        >
                          {deletingComment === comment.id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <img 
                                src="https://unpkg.com/pixelarticons@1.8.0/svg/trash.svg" 
                                alt="Delete" 
                                className="w-4 h-4"
                                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)' }}
                              />
                              Delete
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Reply form */}
                    {replyingTo === comment.id && (
                      <form onSubmit={handleSubmitReply} className="mt-4 space-y-3">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a reply... (10-1000 characters)"
                          className="w-full p-3 bg-theme-surface border-2 border-theme-border rounded-md text-theme-text resize-vertical min-h-[80px] focus:border-theme-primary focus:outline-none transition-colors"
                          maxLength={1000}
                        />
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${
                            replyText.length < 10 ? 'text-theme-text-muted' : 
                            replyText.length > 900 ? 'text-theme-warning' : 'text-theme-text-secondary'
                          }`}>
                            {replyText.length}/1000 characters
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText('');
                              }}
                              className="px-3 py-1 text-sm text-theme-text-muted hover:text-theme-text transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={submitting || replyText.length < 10 || replyText.length > 1000}
                              className="px-3 py-1 text-sm bg-theme-primary text-white hover:bg-theme-secondary transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {submitting ? 'Posting...' : 'Reply'}
                            </button>
                          </div>
                        </div>
                      </form>
                    )}
                    
                    {/* Replies */}
                    {comments.filter(reply => reply.parent_comment_id === comment.id).length > 0 && (
                      <div className="mt-4 pl-4 border-l-2 border-theme-border space-y-3">
                        {comments.filter(reply => reply.parent_comment_id === comment.id).map((reply) => (
                          <div key={reply.id} className="bg-theme-surface border border-theme-border rounded-lg p-3">
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-theme-secondary text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                {getInitials(reply.user)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-theme-text text-sm">
                                    {reply.user.full_name || reply.user.username}
                                  </span>
                                  <span className="text-xs text-theme-text-muted">
                                    {formatDate(reply.created_at)}
                                  </span>
                                </div>
                                <p className="text-theme-text-secondary text-sm leading-relaxed">{reply.comment}</p>
                                
                                {/* Admin Delete Button for Replies */}
                                {isAdmin && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleDeleteComment(reply.id)}
                                      disabled={deletingComment === reply.id}
                                      className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Delete reply (Admin only)"
                                    >
                                      {deletingComment === reply.id ? (
                                        <>
                                          <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                                          Deleting...
                                        </>
                                      ) : (
                                        <>
                                          <img 
                                            src="https://unpkg.com/pixelarticons@1.8.0/svg/trash.svg" 
                                            alt="Delete" 
                                            className="w-3 h-3"
                                            style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)' }}
                                          />
                                          Delete
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 